/**
 * Goods receipts.
 *
 * Receipting is where the accounting stops being about intention and starts
 * being about facts. Once goods have arrived, the expense belongs to that
 * period whether or not the invoice has turned up — so a receipt posts a
 * goods-received-not-invoiced accrual, and the invoice later unwinds it.
 *
 * Orders sitting unreceipted are the classic month-end problem, so they get
 * their own view with an age against each one.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useTerms, lower } from '../lib/tenant';
import { date, money, statusLabel, statusTone, type BadgeTone } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, Money, PageHeader, Tabs } from '../components/ui';

interface ReceiptRow {
  id: string;
  grn_no: string;
  receipt_date: string;
  /** 'receipt' or 'return'. A return is stored as a negative receipt. */
  receipt_type: string;
  /** 'draft', 'confirmed' or 'reversed'. */
  status: string;
  delivery_note_ref: string | null;
  order_no: string;
  order_id: string;
  supplier_name: string;
  received_by_name: string;
  value_minor: number;
}

interface OutstandingRow {
  /**
   * The ORDER id, not the line id — the endpoint selects po.id, and carries the
   * line's own key separately as line_id. The reference in this row links to
   * the order, so it is the order id that is wanted.
   */
  id: string;
  order_no: string;
  order_date: string;
  supplier_name: string;
  line_id: string;
  line_no: number;
  description: string;
  quantity: string;
  quantity_received: string;
  outstanding_minor: number;
  cost_centre_code: string;
  cost_centre_name: string;
  days_outstanding: number;
}

/**
 * The tone of a receipt's status badge.
 *
 * The shared statusTone has no entry for 'confirmed', so a settled receipt
 * would wear exactly the grey of a draft one — the finished state being the
 * only unmarked state in the column. It is named here rather than in the shared
 * helper because 'confirmed' does not mean the same thing in every ledger.
 */
function receiptTone(status: string): BadgeTone {
  return status === 'confirmed' ? 'ok' : statusTone(status);
}

/**
 * How long an order line has been waiting, in words.
 *
 * An order raised and issued during a demonstration appears here the same
 * minute, so both nought and one have to read properly: "Today", then "1 day",
 * never "0 days" or "1 days".
 */
function ageLabel(days: number): string {
  if (days <= 0) return 'Today';
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function ReceiptsPage() {
  const t = useTerms();
  const [tab, setTab] = useState('recorded');
  const recorded = useAsync(() => api.get<{ receipts: ReceiptRow[] }>('/procurement/receipts'), []);
  const outstanding = useAsync(
    () =>
      api.get<{ lines: OutstandingRow[]; totalOutstandingMinor: number }>('/procurement/outstanding-receipts'),
    [],
  );

  return (
    <>
      <PageHeader
        title="Goods receipts"
        subtitle={`Confirming what actually arrived. A receipt accrues the cost into the period the goods were delivered, before the ${lower(
          t.supplier,
        )} invoice arrives.`}
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'recorded', label: 'Recorded' },
          {
            key: 'outstanding',
            label: 'Awaiting receipt',
            badge: outstanding.data?.lines?.length ?? undefined,
          },
        ]}
      />

      {tab === 'recorded' ? (
        <Card title="Recorded receipts" flush>
          <AsyncPanel state={recorded}>
            {(data) => (
              <DataTable
                caption={`Goods receipts recorded in this ${lower(t.entity)}`}
                rows={data.receipts}
                rowKey={(r) => r.id}
                emptyMessage="No goods receipts have been recorded yet."
                columns={[
                  {
                    key: 'grn',
                    header: 'GRN',
                    // Wide enough for GRN-2026-27-000365 on one line; a document
                    // reference broken mid-token reads as an unfinished layout.
                    width: '11rem',
                    render: (r) => (
                      <>
                        {/*
                         * There is no goods-receipt view in the product yet, so
                         * the reference goes to the order, whose detail lists
                         * this GRN. No document number on the screen is a dead
                         * end, and none of them promises a page that is missing.
                         */}
                        <Link
                          to={`/orders/${r.order_id}`}
                          title={`Open ${r.order_no}, the ${lower(t.purchaseOrder)} this receipt was made against`}
                        >
                          <span className="mono">{r.grn_no}</span>
                        </Link>
                        {r.receipt_type === 'return' ? (
                          <div className="smaller muted">Return to {lower(t.supplier)}</div>
                        ) : null}
                      </>
                    ),
                  },
                  {
                    key: 'order',
                    header: t.purchaseOrder,
                    render: (r) => (
                      <>
                        <Link to={`/orders/${r.order_id}`}>
                          <span className="mono">{r.order_no}</span>
                        </Link>
                        <div className="smaller muted truncate">{r.supplier_name}</div>
                      </>
                    ),
                  },
                  { key: 'date', header: 'Received', render: (r) => date(r.receipt_date) },
                  { key: 'by', header: 'By', render: (r) => <span className="smaller">{r.received_by_name}</span> },
                  {
                    key: 'note',
                    header: 'Delivery note',
                    render: (r) => <span className="mono smaller">{r.delivery_note_ref ?? '—'}</span>,
                  },
                  { key: 'value', header: 'Value', numeric: true, render: (r) => <Money minor={r.value_minor} /> },
                  {
                    /*
                     * The receipt's own state, not its type.
                     *
                     * This column used to take its label from receipt_type and
                     * its colour from status, so a reversed GRN came out as a
                     * red badge reading "Receipt" and the state a reader
                     * actually needs — draft, confirmed, reversed — appeared
                     * nowhere on the screen. A return is named under the GRN
                     * instead, which is what explains the negative value
                     * further along its row.
                     */
                    key: 'status',
                    header: 'Status',
                    render: (r) => <Badge tone={receiptTone(r.status)}>{statusLabel(r.status)}</Badge>,
                  },
                ]}
                footer={
                  /*
                   * The value of the receipts listed, and deliberately not
                   * labelled as the goods-received-not-invoiced accrual: several
                   * of these have since been invoiced, which unwinds the
                   * accrual. The accrual balance is a control account in the
                   * ledger, not the sum of this list.
                   *
                   * DataTable shows its empty state instead of the table when
                   * there are no rows, so this never appears without figures
                   * above it.
                   */
                  <tr className="is-total">
                    <td colSpan={5}>Total value received</td>
                    <td className="num">{money(data.receipts.reduce((sum, r) => sum + r.value_minor, 0))}</td>
                    <td />
                  </tr>
                }
              />
            )}
          </AsyncPanel>
        </Card>
      ) : (
        <Card
          title="Ordered but not yet received"
          subtitle="A month-end question: has this arrived, and should the cost be accrued?"
          flush
        >
          <AsyncPanel state={outstanding}>
            {(data) => (
              <DataTable
                caption={`${t.purchaseOrder} lines awaiting receipt`}
                rows={data.lines}
                rowKey={(l) => l.line_id}
                emptyMessage="Everything ordered has been received."
                columns={[
                  {
                    key: 'order',
                    header: t.purchaseOrder,
                    render: (l) => (
                      <>
                        <Link to={`/orders/${l.id}`}>
                          <span className="mono">{l.order_no}</span>
                        </Link>
                        <div className="smaller muted truncate">{l.supplier_name}</div>
                      </>
                    ),
                  },
                  { key: 'desc', header: 'Line', render: (l) => <span className="truncate">{l.description}</span> },
                  {
                    key: 'cc',
                    header: t.costCentre,
                    // The code alone does not tell a budget holder whether the
                    // outstanding commitment is hers, and the server returns the
                    // name on the same row.
                    render: (l) => (
                      <>
                        <div className="mono smaller">{l.cost_centre_code}</div>
                        <div className="smaller muted truncate">{l.cost_centre_name}</div>
                      </>
                    ),
                  },
                  { key: 'qty', header: 'Ordered', numeric: true, render: (l) => l.quantity },
                  { key: 'recd', header: 'Received', numeric: true, render: (l) => l.quantity_received },
                  {
                    key: 'value',
                    header: 'Outstanding',
                    numeric: true,
                    render: (l) => <Money minor={l.outstanding_minor} />,
                  },
                  {
                    key: 'age',
                    header: 'Age',
                    numeric: true,
                    // Past about two months an unreceipted order has stopped
                    // being in transit and become a question for the close.
                    render: (l) => (
                      <span className={l.days_outstanding > 60 ? 'num--negative' : undefined}>
                        {ageLabel(l.days_outstanding)}
                      </span>
                    ),
                  },
                ]}
                footer={
                  // The server totals this itself, so the figure in the footer
                  // is the same one the endpoint stands behind rather than a
                  // second opinion computed in the browser.
                  <tr className="is-total">
                    <td colSpan={5}>Total awaiting receipt</td>
                    <td className="num">{money(data.totalOutstandingMinor)}</td>
                    <td />
                  </tr>
                }
              />
            )}
          </AsyncPanel>
        </Card>
      )}
    </>
  );
}
